import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { Answers, Assessment } from '@asvise/household-engine/vocabulary'
import { sessionToken } from './session-token'

/**
 * The rules, run where the household cannot read them.
 *
 * The same arrangement the business app arrived at. The engine is the product,
 * and running it in the browser would compile every rule about what a family
 * needs into the JavaScript any visitor downloads.
 *
 * Authenticated even though it reads nothing and writes nothing. Without that
 * it is a free oracle: anybody could post answers and read back the judgement,
 * one request at a time, which is most of what the rule set is worth.
 */
export async function assess(answers: Answers, now?: string): Promise<Assessment> {
  const token = await sessionToken()
  const response = await fetch('/assess', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ answers, now }),
  })

  if (!response.ok) {
    const said = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(said?.error ?? `That could not be assessed (${response.status}).`)
  }

  const body = (await response.json()) as { assessment: Assessment }
  return body.assessment
}

/**
 * A short, stable name for a set of answers, for the query key.
 *
 * FNV-1a over the serialised answers, the business app's. The key has to change
 * exactly when the answers do, and the answers are a few kilobytes, which is a
 * poor thing to compare structurally on every render. A collision would mean
 * showing one screening's result for another, and both would be this account's
 * and differ by a keystroke.
 */
export function keyFor(value: unknown): string {
  const text = JSON.stringify(value)
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(36)
}

/** How long the board may quietly show the previous answer's figures. */
const LAGGING_MS = 1500

export interface Assessed {
  /** The screening as it stands, or null before the first answer comes back. */
  current: Assessment | null
  /**
   * Behind for long enough that somebody might act on it.
   *
   * Not simply "a request is in flight": at the usual couple of hundred
   * milliseconds a board that announced every settled answer would read as
   * broken when it is merely working. This is the threshold between a screen
   * catching up and a screen that is wrong, and it is a guess.
   */
  lagging: boolean
  /** The request failed, or there is no network. */
  failed: boolean
  retry: () => void
}

/**
 * The board's screening, and the truth about how current it is.
 *
 * The business app's hook, and `keepPreviousData` is what makes a live board
 * bearable: without it every settled answer blanks the board and redraws it,
 * which is a worse lie than figures a few hundred milliseconds old. An empty
 * board says there is nothing to report, and there is.
 */
export function useAssessment(answers: Answers | null): Assessed {
  const key = useMemo(() => (answers ? keyFor(answers) : 'none'), [answers])

  const query = useQuery({
    queryKey: ['assess', key],
    queryFn: () => assess(answers as Answers),
    enabled: answers !== null,
    placeholderData: keepPreviousData,
    /**
     * The rules are pure given the answers and the instant, and the instant
     * only matters where a fact expires, which is months away. So a result does
     * not go off, and refetching on a window focus would be a round trip to be
     * told what is already on screen.
     */
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  const behind = query.isPlaceholderData && query.isFetching
  const [lagging, setLagging] = useState(false)
  const since = useRef<number | null>(null)

  useEffect(() => {
    if (!behind) {
      since.current = null
      setLagging(false)
      return
    }
    since.current ??= Date.now()
    const left = LAGGING_MS - (Date.now() - since.current)
    if (left <= 0) {
      setLagging(true)
      return
    }
    const timer = setTimeout(() => setLagging(true), left)
    return () => clearTimeout(timer)
  }, [behind])

  return {
    /**
     * Nothing, where there is nothing to screen.
     *
     * `keepPreviousData` is what makes a live board bearable, and it is also
     * what served a screening after the answers it was made from had gone:
     * disabling the query still hands back the last result as the new key's
     * placeholder. The caller passes null when there is nothing to screen, and
     * the previous household's figures were still being read out of here.
     *
     * Found on the agreement that used to gate this. Withdrawing it set the
     * answers to null, the board went because it was gated directly, and the
     * lines on the record naming which areas read an answer stayed exactly
     * where they were. The agreement is gone; the bug was never about it.
     */
    current: answers === null ? null : (query.data ?? null),
    lagging,
    failed: query.isError,
    retry: () => void query.refetch(),
  }
}
